import { useMutation } from '@tanstack/react-query';
import { QUERY, useSuspenseQueryOperation } from 'api/Query';
import type { Callback } from 'ts/base/Callback';
import { Links } from 'ts/commons/links/Links';
import { openModal } from 'ts/commons/modal/ModalUtils';
import { NavigationUtils } from 'ts/commons/NavigationUtils';
import { StringUtils } from 'ts/commons/StringUtils';
import { Button } from 'ts/components/Button';
import { ScrollArea } from 'ts/components/Modal';
import { type TaskDataContainer } from 'ts/data/TaskDataContainer';
import { TaskTable } from 'ts/perspectives/quality_control/tasks/dialog/TaskSelectionDialogTable';
import { TaskUtils } from 'ts/perspectives/quality_control/tasks/utils/TaskUtils';
import { ETaskStatus } from 'typedefs/ETaskStatus';

type TaskSelectionDialogProps = {
	projectId: string;
	taskDetails: TaskDataContainer;
	onTaskUpdated?: Callback<number>;
	close: () => void;
};

/** Function to open Task Selection Dialog for adding tasks */
export function openTaskSelectionModal(
	projectId: string,
	taskDetails: TaskDataContainer,
	onTaskUpdated: Callback<number> = taskId => TaskUtils.navigateToTaskDetailView(projectId, taskId, false, true)
) {
	const title = `Add ${taskDetails.codeSnippet != null ? 'code snippet' : StringUtils.pluralize(taskDetails.findings, 'finding')} to task`;
	openModal({
		scrollAreaComponent: ScrollArea.Autosize,
		title,
		contentRenderer: close => (
			<TaskSelectionDialog
				projectId={projectId}
				taskDetails={taskDetails}
				onTaskUpdated={onTaskUpdated}
				close={close}
			/>
		)
	});
}

/** Task selection dialog, with add functionality depending on enableAddTaskButton */
function TaskSelectionDialog({ projectId, taskDetails, onTaskUpdated, close }: Required<TaskSelectionDialogProps>) {
	const resolvedTasks = useSuspenseQueryOperation(
		QUERY.getResolvedTasks(projectId, { status: [ETaskStatus.OPEN.name] })
	);

	const { mutate } = useAddToTaskMutation(projectId, taskDetails, onTaskUpdated, close);

	return (
		<>
			<Button
				compact
				content="New Task"
				icon="tasks"
				onClick={() => {
					TaskUtils.putTaskDataInSessionStore(taskDetails);
					NavigationUtils.openInNewTab(Links.createTask(projectId, undefined, true), true);
					close();
				}}
			/>
			{resolvedTasks.length > 0 ? ' or choose an existing tasks:' : null}
			{resolvedTasks.length > 0 ? <TaskTable tasks={resolvedTasks} onSelectTask={mutate} /> : null}
		</>
	);
}

function useAddToTaskMutation(
	projectId: string,
	taskDetails: TaskDataContainer,
	onTaskUpdated: (result: number) => void,
	close: () => void
) {
	return useMutation({
		mutationFn: async (taskId: number) => {
			const retrievedTask = await QUERY.getTask(projectId, taskId, {}).fetch();
			const task = retrievedTask.task;
			if (task.findings == null) {
				task.findings = [];
			}
			const existingFindingIds = task.findings.map(finding => finding.findingId);
			for (const finding of taskDetails.findings) {
				if (!existingFindingIds.includes(finding.id)) {
					task.findings.push({
						findingId: finding.id,
						branchName: TaskUtils.getFindingBranchName(finding, taskDetails.branch)
					});
				}
			}
			if (taskDetails.codeSnippet) {
				task.codeSnippets!.push(taskDetails.codeSnippet);
			}
			return QUERY.updateTask(projectId, task.id, {}, task).fetch();
		},
		onSuccess: task => {
			for (const finding of taskDetails.findings) {
				void QUERY.getTasksForFinding(projectId, finding.id, {}).invalidate({
					ignoreQueryParamsAndBody: true
				});
			}
			onTaskUpdated(task.id);
			close();
		}
	});
}
